import { useId, useState } from 'react'

// "Compare another place" — P5-3 T9, and the only door into this feature.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SECOND FIELD IS THE ONE THAT MATTERS, AND IT ONLY EXISTS ONCE
// ═══════════════════════════════════════════════════════════════════════════
//
// A trip that has never compared anything has entries attached to nothing —
// which is exactly what makes progressive disclosure work ("a single-
// destination trip simply has one destination and never renders the concept").
// So the first time somebody wants a second place, the board's existing
// contents need a name too, or the comparison opens with one full column and
// one empty one and no explanation.
//
// Asking for it is one extra field, once, at the only moment the question makes
// sense: *you have been planning something — what do we call it?* The Function
// attaches every loose entry to that name in the same call.
//
// After that the form is one field forever, because the question has an answer.
//
// ⚠️ NO PREVIEW, NO SUGGESTION, NO GUESSED NAME. The board's title is not the
// place's name — "Portugal, the big one" is a trip, "Portugal" is a place —
// and prefilling one with the other would put a word on the screen that
// somebody has to notice is wrong before it becomes permanent.

export default function AddPlace({ needsExisting, onAdd, busy = false, onCancel }) {
  const formId = useId()
  const [name, setName] = useState('')
  const [existing, setExisting] = useState('')

  const submit = async (event) => {
    event.preventDefault()
    if (name.trim() === '') return
    if (needsExisting && existing.trim() === '') return

    const body = { name: name.trim() }
    if (needsExisting) body.existing_name = existing.trim()

    const ok = await onAdd?.(body)
    if (ok) {
      setName('')
      setExisting('')
    }
  }

  return (
    <form className="d2d-addplace" onSubmit={submit} aria-label="Compare another place">
      {needsExisting ? (
        <p className="d2d-addplace__field">
          <label className="d2d-micro" htmlFor={`${formId}-existing`}>
            What is already on the board
          </label>
          <input
            id={`${formId}-existing`}
            value={existing}
            required
            maxLength={200}
            onChange={(event) => setExisting(event.target.value)}
            placeholder="Portugal"
          />
          <span className="d2d-addplace__hint">
            Everything on the board goes with this one. Nothing moves off it.
          </span>
        </p>
      ) : null}

      <p className="d2d-addplace__field">
        <label className="d2d-micro" htmlFor={`${formId}-name`}>
          The other place
        </label>
        <input
          id={`${formId}-name`}
          value={name}
          required
          maxLength={200}
          onChange={(event) => setName(event.target.value)}
          placeholder="Japan"
        />
      </p>

      <p className="d2d-addplace__actions">
        <button type="submit" className="d2d-addplace__submit" disabled={busy}>
          Compare it
        </button>
        {onCancel ? (
          <button type="button" className="d2d-addplace__cancel" onClick={onCancel} disabled={busy}>
            Not now
          </button>
        ) : null}
      </p>
    </form>
  )
}
